import { useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Alert, Form } from 'reactstrap'

import { API } from 'API'
import { RequestToasterContext } from 'containers/Providers'
import {
  FeatureFlagsSchema,
  FeatureFlagsSchemaEntry,
  FeatureFlagsSchemaObjectEntry,
  FeatureFlagsWithSchema,
  GenericFeatureFlags,
} from 'domains/featureFlags'
import { StandardErrorResponse } from 'types/APIResponses'
import { getValueByPath } from 'utils/getValueByPath'

import { FeatureFlagRootObject } from './FeatureFlagTree'
import { featureFlagsDiff } from './utils/featureFlagsDiff'
import { mergeFFArrays } from './utils/mergeFFArrays'

interface PermissionsControlProps {
  username: string
}

export const PermissionsControl = ({ username }: PermissionsControlProps) => {
  const { requestStatusRef } = useContext(RequestToasterContext)

  const [schemaError, setSchemaError] = useState<string | undefined>(undefined)
  const [dbFFs, setDbFFs] = useState<GenericFeatureFlags>()
  const [ffsSchema, setFfsSchema] = useState<FeatureFlagsSchema>()
  const form = useForm<GenericFeatureFlags>({
    mode: 'onSubmit',
  })

  const fillFormWithValues = (
    path: string[],
    ffSchemaNode: FeatureFlagsSchemaObjectEntry | FeatureFlagsSchemaEntry,
    serverData: FeatureFlagsWithSchema,
  ) => {
    if (ffSchemaNode.type === 'object') {
      for (const property of Object.entries(ffSchemaNode.properties)) {
        fillFormWithValues([...path, property[0]], property[1], serverData)
      }

      return
    }

    if (ffSchemaNode.readOnly) {
      return
    }

    const fullPath = path.join('.')

    const valueToSet =
      ffSchemaNode.type === 'array'
        ? mergeFFArrays(serverData.override, serverData.data, fullPath)
        : (getValueByPath(serverData.override, fullPath) ??
          getValueByPath(serverData.data, fullPath))

    const valueToSetOrDefault = valueToSet ?? ffSchemaNode.default

    form.register(fullPath)

    if (valueToSetOrDefault == undefined) {
      return
    }

    form.setValue(fullPath, valueToSetOrDefault)
  }

  const applyServerData = (serverData: FeatureFlagsWithSchema) => {
    setSchemaError(undefined)

    try {
      fillFormWithValues([], serverData.schema, serverData)

      setFfsSchema(serverData.schema)
      setDbFFs(serverData.data)
    } catch (e) {
      if (e instanceof Error) {
        setSchemaError(e.message ?? 'Unknown error')
      } else {
        setSchemaError('Unknown error')
      }
    }
  }

  const doUpdateFeatureFlagsOverride = (username: string, override: Record<string, unknown>) =>
    API.updateFeatureFlagsOverride(username, override)
      .then((response) => {
        applyServerData(response)
        requestStatusRef.current?.showAlert('Permissions updated', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })

  useEffect(() => {
    API.getUsersFeatureFlagsWithSchema(username)
      .then(applyServerData)
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, requestStatusRef, form, setDbFFs, setFfsSchema])

  const updatePermissions = () => {
    if (!dbFFs) {
      return
    }

    const overrideValues = form.watch()
    const ffDiff = featureFlagsDiff(dbFFs, overrideValues)

    if (!ffDiff) {
      return
    }

    doUpdateFeatureFlagsOverride(username, ffDiff)
  }

  const restoreDefaults = () => {
    if (!ffsSchema || !dbFFs) {
      return
    }

    doUpdateFeatureFlagsOverride(username, {})
  }

  return (
    <FormProvider {...form}>
      {schemaError && <Alert color="danger">{schemaError}</Alert>}

      {ffsSchema && (
        <Form>
          <FeatureFlagRootObject ffSchemaObject={ffsSchema} dbFFObject={dbFFs ?? {}} />
        </Form>
      )}

      <div className="d-flex justify-content-end mt-3" style={{ gap: '1rem' }}>
        <button disabled={!ffsSchema} onClick={restoreDefaults}>
          Restore Defaults
        </button>
        <button disabled={!ffsSchema} onClick={updatePermissions}>
          Update Feature Permissions
        </button>
      </div>
    </FormProvider>
  )
}
